import { Input } from '@trellis/roots/input'
import { Label } from '@trellis/roots/label'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@trellis/roots/tooltip'
import { Typography } from '@trellis/roots/typography'
import { Check, Info, Loader2, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useInvoiceNumberAvailabilityQuery } from '@/hooks/useInvoices'

export interface InvoiceNumberFieldProps {
  id: string
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  /** Value the field is seeded with once it becomes available. */
  prefillValue?: string
  /** The invoice's own current number — skips the availability check when unchanged. */
  originalValue?: string
  disclaimer: string
  error?: string
  /**
   * Surfaces whether the current value is safe to submit (unavailable or still checking
   * blocks it).
   */
  onAvailabilityChange?: (isBlocking: boolean) => void
}

/**
 * Invoice number field, shared by invoice create, DRAFT edit, and the duplicate-invoice
 * dialog — each supplies its own prefill source and disclaimer copy. Debounces a
 * real-time uniqueness check against `GET /invoices/number-availability`; the DB
 * `@unique` constraint stays the authoritative check on submit.
 *
 * @see specs/README.md → invoices/ → "Reserve Historical Invoice Numbers on Import"
 */
export function InvoiceNumberField({
  id,
  value,
  onChange,
  onBlur,
  prefillValue,
  originalValue,
  disclaimer,
  error,
  onAvailabilityChange,
}: InvoiceNumberFieldProps) {
  const [hasPrefilled, setHasPrefilled] = useState(!!value)
  useEffect(() => {
    if (hasPrefilled || value || !prefillValue) return
    onChange(prefillValue)
    setHasPrefilled(true)
  }, [value, prefillValue, hasPrefilled, onChange])

  function handleBlur() {
    if (!value.trim()) {
      const fallback = originalValue ?? prefillValue
      if (fallback) onChange(fallback)
    }
    onBlur?.()
  }

  const trimmedValue = value.trim()
  const unchanged = originalValue !== undefined && trimmedValue === originalValue.trim()
  const { data: availabilityResult, isFetching } = useInvoiceNumberAvailabilityQuery(
    trimmedValue,
    !unchanged,
  )
  const isChecking = !unchanged && trimmedValue.length > 0 && isFetching
  const isAvailable =
    !unchanged && !isFetching && availabilityResult?.data?.available === true
  const isTaken =
    !unchanged && !isFetching && availabilityResult?.data?.available === false

  useEffect(() => {
    onAvailabilityChange?.(isChecking || isTaken)
  }, [isChecking, isTaken, onAvailabilityChange])

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Label htmlFor={id}>Invoice reference #</Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                aria-label="More information"
              >
                <Info className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{disclaimer}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="relative">
        <Input
          id={id}
          value={value}
          onChange={(changeEvent) => onChange(changeEvent.target.value)}
          onBlur={handleBlur}
          maxLength={50}
          aria-invalid={isTaken || !!error}
        />
        {!unchanged && trimmedValue.length > 0 && (
          <div className="absolute inset-y-0 right-2 flex items-center">
            {isChecking && (
              <Loader2 className="text-muted-foreground size-4 animate-spin" />
            )}
            {isAvailable && (
              <span className="flex size-4 items-center justify-center rounded-full bg-emerald-600">
                <Check className="size-2.5 text-white" strokeWidth={3} />
              </span>
            )}
            {isTaken && (
              <span className="bg-destructive flex size-4 items-center justify-center rounded-full">
                <X className="text-destructive-foreground size-2.5" strokeWidth={3} />
              </span>
            )}
          </div>
        )}
      </div>
      {isTaken && (
        <Typography variant="body" color="destructive-text">
          This invoice reference is already in use
        </Typography>
      )}
      {error && !isTaken && (
        <Typography variant="body" color="destructive-text">
          {error}
        </Typography>
      )}
    </div>
  )
}
